/**
 * Bug Explorer product modules:
 *  <be-buy-box>    real product form: variants, pack tiers, quantity, add-ons, Ajax add to cart
 *  <be-sticky-buy> sticky purchase bar mirroring the primary buy box
 * All prices/IDs come from Liquid-rendered Shopify data; nothing is hardcoded here.
 */
import { money, onScrollFrame } from '@be/core';
import { addToCart } from '@be/cart';

const fill = (template, key, value) => String(template || '').split(`[${key}]`).join(String(value));

/* ======================================================================== */
class BeBuyBox extends HTMLElement {
  /** @type {AbortController | null} */ #ac = null;
  multiplier = 1;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    try {
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-be-form]'));
    if (!this.form) return;
    const current = Number(this.form.querySelector('[data-variant-input]')?.getAttribute('value'));
    this.variant = this.variants.find((v) => v.id === current) || this.variants[0];

    this.form.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    document.addEventListener('be:pack-select', (event) => this.#onPackSync(/** @type {CustomEvent} */ (event)), { signal });
    this.update(false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get productId() {
    return this.dataset.productId;
  }

  /** @returns {string[]} */
  selectedOptions() {
    const base = this.variant ? [...this.variant.options] : [];
    const packPos = Number(this.dataset.packPosition || 0);
    this.querySelectorAll('[data-option-position]').forEach((fieldset) => {
      const pos = Number(fieldset.getAttribute('data-option-position'));
      const checked = /** @type {HTMLInputElement|null} */ (fieldset.querySelector('input:checked'));
      if (checked) base[pos - 1] = checked.value;
    });
    if (packPos) {
      const checked = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-pack-input]:checked'));
      if (checked) base[packPos - 1] = checked.value;
    }
    return base;
  }

  findVariant(options) {
    return this.variants.find((v) => v.options.every((o, i) => o === options[i]));
  }

  tierQuantity() {
    if (this.dataset.mode !== 'tiers') return 1;
    const checked = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-pack-input]:checked'));
    return Math.max(1, Number(checked?.dataset.qty || 1));
  }

  /** Current selection summary, consumed by the sticky bar. */
  get state() {
    const v = this.variant;
    const tierQty = this.tierQuantity();
    const units = tierQty * this.multiplier;
    const total = v ? v.price * units : 0;
    const compare = v && v.compare > v.price ? v.compare * units : 0;
    const label = this.querySelector('[data-pack-input]:checked')?.closest('.be-pack')?.querySelector('.be-pack__label')?.textContent?.trim() || '';
    return { variant: v, units, total, compare, label, multiplier: this.multiplier, available: Boolean(v?.available) };
  }

  /** @param {boolean} fromUser */
  update(fromUser) {
    const options = this.selectedOptions();
    const variant = this.findVariant(options) || null;
    if (variant) this.variant = variant;
    const v = variant;
    const tierQty = this.tierQuantity();

    const idInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-variant-input]'));
    const qtyInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-qty-input]'));
    if (idInput && v) idInput.value = String(v.id);
    if (qtyInput) qtyInput.value = String(tierQty * this.multiplier);
    const qtyValue = this.querySelector('[data-qty-value]');
    if (qtyValue) qtyValue.textContent = String(this.multiplier);
    this.querySelector('[data-qty-step="-1"]')?.toggleAttribute('disabled', this.multiplier <= 1);

    // Option legends
    this.querySelectorAll('[data-option-position]').forEach((fieldset) => {
      const checked = /** @type {HTMLInputElement|null} */ (fieldset.querySelector('input:checked'));
      const out = fieldset.querySelector('[data-option-value]');
      if (out && checked) out.textContent = checked.value;
    });

    if (v) {
      // Headline price = selected pack.
      const total = v.price * tierQty;
      const compare = v.compare > v.price ? v.compare * tierQty : 0;
      const pct = compare ? Math.round(((compare - total) / compare) * 100) : 0;
      this.#text('[data-price]', money(total));
      this.#toggle('[data-compare]', compare > 0, money(compare));
      this.#toggle('[data-save-badge]', pct > 0, fill(this.dataset.save, 'percent', pct));
      this.#text('[data-unit-line]', tierQty > 1 ? fill(this.dataset.perUnit, 'price', money(v.price)) : '');

      // Pack cards
      if (this.dataset.mode === 'tiers') {
        this.querySelectorAll('[data-pack-input]').forEach((input) => {
          const card = input.closest('.be-pack');
          if (!card || !(input instanceof HTMLInputElement)) return;
          const q = Math.max(1, Number(input.dataset.qty || 1));
          const t = v.price * q;
          const c = v.compare > v.price ? v.compare * q : 0;
          const p = c ? Math.round(((c - t) / c) * 100) : 0;
          card.querySelector('[data-pack-price]').textContent = money(t);
          this.#toggleIn(card, '[data-pack-compare]', c > 0, money(c));
          this.#toggleIn(card, '[data-pack-save]', p > 0, fill(this.dataset.save, 'percent', p));
          const unit = card.querySelector('[data-pack-unit]');
          if (unit) unit.textContent = fill(this.dataset.perUnit, 'price', money(v.price));
          card.classList.toggle('is-unavailable', !v.available);
        });
      } else if (this.dataset.mode === 'variants') {
        const packPos = Number(this.dataset.packPosition || 0) - 1;
        this.querySelectorAll('[data-pack-input]').forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          const card = input.closest('.be-pack');
          const opts = [...options];
          opts[packPos] = input.value;
          const pv = this.findVariant(opts);
          if (!card) return;
          card.classList.toggle('is-unavailable', !pv || !pv.available);
          if (!pv) return;
          const c = pv.compare > pv.price ? pv.compare : 0;
          const p = c ? Math.round(((c - pv.price) / c) * 100) : 0;
          card.querySelector('[data-pack-price]').textContent = money(pv.price);
          this.#toggleIn(card, '[data-pack-compare]', c > 0, money(c));
          this.#toggleIn(card, '[data-pack-save]', p > 0, fill(this.dataset.save, 'percent', p));
        });
      }
    }

    // Availability
    const available = Boolean(v && v.available);
    const button = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-add-button]'));
    if (button) {
      button.disabled = !available;
      const label = button.querySelector('[data-add-label]');
      if (label) label.textContent = available ? this.dataset.addLabel || '' : v ? this.dataset.outOfStock || '' : '';
    }
    const stock = this.querySelector('[data-stock]');
    const stockText = this.querySelector('[data-stock-text]');
    if (stock && stockText) {
      const threshold = Number(this.dataset.lowThreshold || 0);
      let text = available ? this.dataset.inStock : this.dataset.outOfStock;
      if (available && v && typeof v.inv === 'number' && threshold > 0 && v.inv > 0 && v.inv <= threshold) {
        text = (this.dataset.lowStock || '').split('7357').join(String(v.inv));
      }
      stockText.textContent = text || '';
      stock.classList.toggle('is-out', !available);
    }

    if (fromUser && v && this.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(v.id));
      window.history.replaceState({}, '', url.toString());
    }

    this.dispatchEvent(
      new CustomEvent('be:selection-change', { bubbles: true, detail: { productId: this.productId, box: this, ...this.state } })
    );
  }

  #text(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }
  #toggle(selector, show, value) {
    this.#toggleIn(this, selector, show, value);
  }
  #toggleIn(root, selector, show, value) {
    const el = root.querySelector(selector);
    if (!el) return;
    el.hidden = !show;
    if (show) el.textContent = value;
  }

  /** @param {Event} event */
  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.matches('[data-pack-input]')) {
      document.dispatchEvent(
        new CustomEvent('be:pack-select', { detail: { productId: this.productId, value: target.value, source: this } })
      );
    }
    this.update(true);
  }

  /** Keeps several buy boxes for the same product (PDP + offer section) in sync. */
  #onPackSync(event) {
    const { productId, value, source } = event.detail || {};
    if (source === this || productId !== this.productId) return;
    const input = /** @type {HTMLInputElement|null} */ (
      Array.from(this.querySelectorAll('[data-pack-input]')).find((el) => /** @type {HTMLInputElement} */ (el).value === value) || null
    );
    if (input && !input.checked) {
      input.checked = true;
      this.update(false);
    }
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const step = event.target instanceof Element ? event.target.closest('[data-qty-step]') : null;
    if (!step) return;
    this.multiplier = Math.min(99, Math.max(1, this.multiplier + Number(step.getAttribute('data-qty-step'))));
    this.update(true);
  }

  /** @param {SubmitEvent} event */
  #onSubmit(event) {
    event.preventDefault();
    const submitter = event.submitter instanceof HTMLElement ? event.submitter : this.querySelector('[data-add-button]');
    this.add(submitter);
  }

  /** Adds the current selection (and checked add-ons) to the real cart. */
  async add(source) {
    const v = this.variant;
    if (!v || !v.available) return;
    const error = this.querySelector('[data-error]');
    if (error) error.hidden = true;
    const items = [{ id: v.id, quantity: this.tierQuantity() * this.multiplier }];
    this.querySelectorAll('[data-addon]:checked').forEach((box) => {
      if (box instanceof HTMLInputElement) items.push({ id: Number(box.value), quantity: 1 });
    });
    const button = this.querySelector('[data-add-button]');
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    try {
      await addToCart(items, { source: source || button });
      this.querySelectorAll('[data-addon]:checked').forEach((box) => {
        if (box instanceof HTMLInputElement) box.checked = false;
      });
    } catch (e) {
      if (error) {
        error.textContent = e instanceof Error ? e.message : String(e);
        error.hidden = false;
      }
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  }
}

/* ======================================================================== */
class BeStickyBuy extends HTMLElement {
  /** @type {AbortController | null} */ #ac = null;
  /** @type {IntersectionObserver | null} */ #io = null;
  #visible = new Set();
  #passed = false;
  /** @type {(() => void) | null} */ #unsub = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const productId = this.dataset.productId;
    const sync = (detail) => {
      if (!detail || detail.productId !== productId) return;
      const price = this.querySelector('[data-sticky-price]');
      const compare = this.querySelector('[data-sticky-compare]');
      const label = this.querySelector('[data-sticky-label]');
      const button = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-sticky-add]'));
      if (price) price.textContent = money(detail.total);
      if (compare instanceof HTMLElement) {
        compare.hidden = !detail.compare;
        compare.textContent = detail.compare ? money(detail.compare) : '';
      }
      if (label) label.textContent = detail.label ? detail.label + (detail.multiplier > 1 ? ` × ${detail.multiplier}` : '') : '';
      if (button) {
        button.disabled = !detail.available;
        const text = button.querySelector('[data-sticky-text]');
        if (text) text.textContent = detail.available ? this.dataset.addLabel || '' : this.dataset.soldOut || '';
      }
    };
    document.addEventListener('be:selection-change', (event) => sync(/** @type {CustomEvent} */ (event).detail), { signal });
    const primary = this.#primary();
    if (primary) sync({ productId, ...primary.state });

    this.addEventListener(
      'click',
      (event) => {
        const add = event.target instanceof Element ? event.target.closest('[data-sticky-add]') : null;
        if (add) this.#primary()?.add(add);
      },
      { signal }
    );

    this.#io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#visible.add(entry.target);
        else this.#visible.delete(entry.target);
      }
      this.#evaluate();
    });
    document.querySelectorAll('[data-be-buy-anchor]').forEach((el) => this.#io?.observe(el));
    this.#unsub = onScrollFrame(() => this.#evaluate());
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
    this.#unsub?.();
  }

  #primary() {
    return /** @type {BeBuyBox|null} */ (document.querySelector(`be-buy-box[data-primary][data-product-id="${this.dataset.productId}"]`));
  }

  #evaluate() {
    const first = document.querySelector('[data-be-buy-anchor]');
    this.#passed = first ? first.getBoundingClientRect().bottom < 0 : false;
    const show = this.#passed && this.#visible.size === 0;
    if (show === this.hasAttribute('data-show')) return;
    this.toggleAttribute('data-show', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
    this.toggleAttribute('inert', !show);
  }
}

if (!customElements.get('be-buy-box')) customElements.define('be-buy-box', BeBuyBox);
if (!customElements.get('be-sticky-buy')) customElements.define('be-sticky-buy', BeStickyBuy);